import React, { useMemo, useRef, useEffect } from "react"
import { Editor as SlateEditor } from "@convertkit/slate-react"
import { createStore } from "core/store"
import { createHTM, html } from "./utils/htm"
import split from "./utils/split"
import { getNodeFromValue } from "./utils/helpers"
import EditorContext, { useValue, useEditor } from "./editor-context"
import RenderContext from "./render-context"
import BlockToolbar, {
  createOption,
  createInsertBelowOption
} from "./components/block-toolbar"
import BlockMenu from "./components/block-menu"
import SelectionToolbar from "./components/selection-toolbar"
import FloatingToolbar from "./components/selection-toolbar/floating-toolbar"
import ElementToolbar from "./components/element-toolbar"
import WordCount from "./components/word-count"
import {
  LinkIconButton,
  ToolbarButton as LinkToolbarButton
} from "./plugins/links"
import TemplateEditor, {
  html as renderTemplate
} from "./template-editor/editor"
import Plugins from "./plugins"
import serialize from "./serializer"
import parse from "./parser/parse"
import { useImageDataURL, ImageData } from "./plugins/images"
import defaultConfig from "./default-config"
import useTheme, {
  ThemeProvider,
  InheritedThemeProvider,
  themeFromTemplateSettings,
  useInheritedTheme,
  createTheme
} from "./hooks/use-theme"
import replaceOutlookTags from "./utils/replace-outlook-tags"
import SlateNode, { useHoverNode, setHoverNode } from "./slate-nodes/slate-node"
import { EditorContent, EditorSidebar } from "./editor-containers"

const empty = []

const { useStore: useCurrentEditor, set: setCurrentEditor } = createStore({})
const { useStore: useCurrentNode, set: setCurrentNode } = createStore({})
const { useStore: useHoverBlock, set: setHoverBlock } = createStore(null)

function Editor({
  children,
  className,
  value,
  plugins = empty,
  config = defaultConfig,
  target = "email",
  onFocus,
  style = {},
  ...props
}) {
  const editor = useRef(null)

  className = [
    className,
    "notranslate" // Prevent browsers from translating text in place.
  ]
    .filter(Boolean)
    .join(" ")

  const currentEditor = useCurrentEditor()
  const currentNode = useCurrentNode()

  useEffect(() => {
    if (!value.startBlock) return

    if (currentEditor !== editor.current) {
      // This guard clause prevents an issue when switching Sequence emails:
      // https://convertkit.slack.com/archives/CHZGVGC2W/p1667855770643919?thread_ts=1667826074.045209&cid=CHZGVGC2W
      if (!value.selection.isFocused) return

      setCurrentEditor(editor.current)
    }

    if (
      value.startBlock.key !== currentNode.key ||
      value.startBlock.type !== currentNode.type ||
      value.startBlock.data !== currentNode.data
    ) {
      setCurrentNode(getNodeFromValue(value))
    }
  }, [value])

  function handleFocus(_event, editor, next) {
    onFocus && onFocus(editor)

    next()
  }

  const activePlugins = useMemo(
    () => Plugins(config, plugins),
    [plugins, config]
  )

  // We want to override this style for non-readonly editors to fix an issue with Chrome v105
  // See https://github.com/ianstormtaylor/slate/issues/5110
  const chromeFix = props.readOnly ? {} : { WebkitUserModify: "read-write" }
  const adjustedStyle = { ...style, ...chromeFix }

  return (
    <EditorContext editor={editor.current} value={value}>
      <RenderContext.Provider value={{ target, mode: "editing" }}>
        <ImageData>
          {typeof children === "function" ? (
            children({
              children: (
                <SlateEditor
                  ref={editor}
                  className={className}
                  value={value}
                  plugins={activePlugins}
                  target={target}
                  onFocus={handleFocus}
                  style={adjustedStyle}
                  {...props}
                />
              )
            })
          ) : (
            <React.Fragment>
              <SlateEditor
                ref={editor}
                className={className}
                value={value}
                plugins={activePlugins}
                target={target}
                onFocus={handleFocus}
                style={adjustedStyle}
                {...props}
              />
              {children}
            </React.Fragment>
          )}
        </ImageData>
      </RenderContext.Provider>
    </EditorContext>
  )
}

export {
  BlockMenu,
  BlockToolbar,
  createHTM,
  createInsertBelowOption,
  createOption,
  createTheme,
  EditorContent,
  EditorSidebar,
  ElementToolbar,
  FloatingToolbar,
  html,
  LinkIconButton,
  LinkToolbarButton,
  parse,
  renderTemplate,
  replaceOutlookTags,
  SelectionToolbar,
  serialize,
  setCurrentEditor,
  SlateEditor,
  split,
  TemplateEditor,
  themeFromTemplateSettings,
  ThemeProvider,
  InheritedThemeProvider,
  useCurrentEditor,
  useCurrentNode,
  useEditor,
  useImageDataURL,
  useInheritedTheme,
  useTheme,
  useValue,
  WordCount,
  useHoverNode,
  setHoverNode,
  useHoverBlock,
  setHoverBlock,
  SlateNode
}

export default Editor
